/**
 * Shared dependencies
 */
import type { WP_Recipe } from '../../shared/types/recipe';

export type GetRecipesParams = {
	full?: 0 | 1; // Returns the full details of the recipe. Default is false.
	ids?: (number | string)[]; // Array of recipe ids to get. If provided, only recipes with these ids will be returned.
	'include-deleted'?: 0 | 1; // Should deleted posts be included. Only applicable if full is not true. Default is false.
	limit?: number; // How many recipe previews to fetch. -1 = no limit. Default is 10.
};

export const DEFAULT_PARAMS: GetRecipesParams = {
	full: 0,
	'include-deleted': 0,
};

/**
 * Fetches the recipes from the Valio API.
 * By default gets the ten (10) latest recipe previews. Return can be changed with query params.
 * @link https://evermade.stoplight.io/docs/valio-app/13801233b1a9d-get-recipes
 * @return {Promise<WP_Recipe[]>} A promise that resolves to an array of recipes.
 */
export async function getRecipes(params: GetRecipesParams = DEFAULT_PARAMS): Promise<WP_Recipe[]> {
	const { session } = window.valioApp;

	// Build query string from params
	const mergedParams = { ...DEFAULT_PARAMS, ...params };
	const query = new URLSearchParams();

	// Add full param
	if (mergedParams.full !== undefined) {
		query.append('full', String(mergedParams.full));
	}

	// Add include-deleted param
	if (mergedParams['include-deleted'] !== undefined) {
		query.append('include-deleted', String(mergedParams['include-deleted']));
	}

	// Add limit param
	if (mergedParams.limit !== undefined) {
		query.append('limit', String(mergedParams.limit));
	}

	// Add ids param as comma-separated string
	if (mergedParams.ids && mergedParams.ids.length > 0) {
		query.append('ids', mergedParams.ids.join(','));
	}

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot create list.'));
	}

	const headers = await session.authHeader();

	return fetch(`/wp-json/valio/app/v1/recipes?${query.toString()}`, {
		method: 'GET',
		headers,
	})
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			return await response.json();
		})
		.catch((error) => {
			console.error('Error fetching user lists:', error);
			throw error;
		});
}
